import { useEffect, useRef, useCallback } from 'react';
import { useTrackListeningTime, type MilestoneCheckCallback } from './useListeningTime';
import { listeningTimeService } from '@/services/listeningTime.service';

interface UseStoryTimeListeningTrackerProps {
  audioRef: React.RefObject<HTMLAudioElement | null>;
  /** Language the story is in. Tracking is skipped when undefined. */
  language: string | undefined;
  /** Must be true before tracking starts (e.g. once audio has been generated). */
  enabled: boolean;
  onMilestoneCheck?: MilestoneCheckCallback;
}

/**
 * useStoryTimeListeningTracker — records StoryTime audio listening time.
 *
 * Mirrors useYouTubeListeningTracker's accumulator/interval/unmount-flush
 * pattern, but is driven by the <audio> element's native play/pause/ended
 * events instead of a global player-state store, since StoryReader owns its
 * own audio element directly.
 *
 * Tracks real elapsed wall-clock time via performance.now() deltas around
 * play/pause/ended — NOT audio.currentTime deltas — so recorded time is
 * independent of playbackRate. 30s of real listening at 1.5x speed records
 * as 30s, not 45s.
 */
export function useStoryTimeListeningTracker({
  audioRef,
  language,
  enabled,
  onMilestoneCheck,
}: UseStoryTimeListeningTrackerProps): void {
  const { mutate: trackListeningTime } = useTrackListeningTime({ onMilestoneCheck });

  const playStartTimeRef = useRef<number | null>(null);
  const accumulatedSecondsRef = useRef<number>(0);
  const trackingIntervalRef = useRef<number | null>(null);

  const languageRef = useRef(language);
  useEffect(() => {
    languageRef.current = language;
  }, [language]);

  const enabledRef = useRef(enabled);
  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  const sendListeningTime = useCallback(() => {
    const seconds = Math.floor(accumulatedSecondsRef.current);
    if (seconds > 0 && language) {
      trackListeningTime({ seconds, language, media_type: 'storytime' });
      accumulatedSecondsRef.current = 0;
    }
  }, [trackListeningTime, language]);

  // Effect 1: attach play/pause/ended listeners to the audio element.
  useEffect(() => {
    if (!enabled) return;
    const el = audioRef.current;
    if (!el) return;

    const handlePlay = () => {
      if (playStartTimeRef.current === null) {
        playStartTimeRef.current = performance.now();
      }
    };
    const handleStop = () => {
      if (playStartTimeRef.current !== null) {
        const elapsed = (performance.now() - playStartTimeRef.current) / 1000;
        accumulatedSecondsRef.current += elapsed;
        playStartTimeRef.current = null;
      }
    };

    el.addEventListener('play', handlePlay);
    el.addEventListener('pause', handleStop);
    el.addEventListener('ended', handleStop);

    return () => {
      el.removeEventListener('play', handlePlay);
      el.removeEventListener('pause', handleStop);
      el.removeEventListener('ended', handleStop);
    };
  }, [audioRef, enabled]);

  // Effect 2: 30-second periodic flush interval — stable for the component's lifetime.
  useEffect(() => {
    if (!enabled) return;

    trackingIntervalRef.current = window.setInterval(() => {
      if (!enabledRef.current) return;
      if (playStartTimeRef.current !== null) {
        const elapsed = (performance.now() - playStartTimeRef.current) / 1000;
        accumulatedSecondsRef.current += elapsed;
        playStartTimeRef.current = performance.now();
      }
      if (accumulatedSecondsRef.current >= 30) {
        sendListeningTime();
      }
    }, 30000);

    return () => {
      if (trackingIntervalRef.current !== null) {
        clearInterval(trackingIntervalRef.current);
        trackingIntervalRef.current = null;
      }
    };
  }, [enabled, sendListeningTime]);

  // Effect 3: flush on unmount via the raw service call (mutations can't run in cleanup).
  useEffect(() => {
    return () => {
      if (playStartTimeRef.current !== null) {
        const elapsed = (performance.now() - playStartTimeRef.current) / 1000;
        accumulatedSecondsRef.current += elapsed;
        playStartTimeRef.current = null;
      }
      if (trackingIntervalRef.current !== null) {
        clearInterval(trackingIntervalRef.current);
        trackingIntervalRef.current = null;
      }
      const seconds = Math.floor(accumulatedSecondsRef.current);
      const lang = languageRef.current;
      if (seconds > 0 && lang) {
        listeningTimeService.track({ seconds, language: lang, media_type: 'storytime' });
        accumulatedSecondsRef.current = 0;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
